import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Popconfirm, Space, Table, message } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useRef, useState } from "react";
import styled from "styled-components";
import { formatDate } from "common/utils";
import { getColumnSearchProps } from "common/antd-utils";
import { TruncatedText } from "components/truncatedText";

const { TextArea } = Input;

const StyledModal = styled(Modal)`
   {
    max-width: 1400px;
    min-width: 320px;
  }
`;
const StyledTextArea = styled(TextArea)`
  min-height: 150px !important;
  max-height: 300px !important;
  overflow-y: auto !important;
`;
const StyledFormItem = styled(Form.Item)`
  margin-bottom: 0px;
  text-align: right;
`;

const StyledDeleteButton = styled(Button)`
  color: inherit;
`;

const StyledAddModal = styled(Modal)`
  .ant-modal-content {
    height: auto !important;
    min-height: unset !important;
  }

  .ant-modal-body {
    overflow: visible !important;
    padding: 0px;
    height: auto !important;
    min-height: unset !important;
  }
`;

const StyledButton = styled(Button)`
  margin-bottom: 12px;
  flex-shrink: 0;
  align-self: flex-start;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    padding: 10px 12px;
    line-height: 1.4;
  }

  .ant-table-tbody > tr > td {
    padding: 6px 10px;
    line-height: 1.4;
  }
`;

const TableContainer = styled.div`
  .ant-table-container {
    height: calc(85vh - 235px);
    min-height: calc(85vh - 235px);
    max-height: calc(85vh - 235px);
    overflow: auto;
  }

  .ant-pagination {
    margin-top: 12px;
    padding: 12px 0 0 0;
    border-top: 1px solid #f0f0f0;
  }
`;

/**
 * A reusable modal component for displaying and managing entity notes in a table format.
 *
 * @param {Object} props
 * @param {string} props.title - The modal title
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onCancel - Callback when modal is closed
 * @param {string} props.entityName - Display name for the entity (e.g., "Client", "Lawsuit")
 * @param {Array} props.notes - Array of note objects
 * @param {boolean} props.isLoading - Whether notes are loading
 * @param {Function} props.onCreateNote - Callback to create a note, receives { message }
 * @param {Function} props.onUpdateNote - Callback to update a note, receives { noteId, message }
 * @param {Function} props.onDeleteNote - Callback to delete a note, receives noteId
 * @param {boolean} props.isCreating - Whether a note is being created
 * @param {boolean} props.isUpdating - Whether a note is being updated
 * @param {Object} props.labels - Custom labels for columns and UI elements
 * @param {string} props.labels.createdAtLabel - Label for created at column (default: "Created At")
 * @param {string} props.labels.createdByLabel - Label for created by column (default: "Created By")
 * @param {string} props.labels.messageLabel - Label for message column (default: "Message")
 * @param {string} props.labels.itemName - Name for the item type (default: "Note", e.g., "Communication Log")
 */
function EntityNotesTableModal({
  title,
  open,
  onCancel,
  entityName = "Entity",
  notes = [],
  isLoading = false,
  onCreateNote,
  onUpdateNote,
  onDeleteNote,
  isCreating = false,
  isUpdating = false,
  labels = {},
}) {
  const {
    createdAtLabel = "Created At",
    createdByLabel = "Created By",
    messageLabel = "Message",
    itemName = "Note",
  } = labels;
  const [form] = useForm();
  const [editingNote, setEditingNote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });

  // Search refs for each column
  const createdBySearchRef = useRef();
  const updatedBySearchRef = useRef();
  const messageSearchRef = useRef();

  const handleAddNote = () => {
    setEditingNote(null);
    form.resetFields();
    setShowModal(true);
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    form.setFieldsValue({ message: note.message });
    setShowModal(true);
  };

  const handleSubmit = async (values) => {
    let result;
    if (!editingNote) {
      result = await onCreateNote({ message: values.message });
    } else {
      result = await onUpdateNote({ noteId: editingNote.id, message: values.message });
    }
    if ("data" in result) {
      message.success(
        `${entityName} ${itemName.toLowerCase()} ${
          !editingNote ? "created" : "updated"
        } successfully`,
      );
      form.resetFields();
      setShowModal(false);
      setEditingNote(null);
    }
  };

  const handleCancelModal = () => {
    form.resetFields();
    setShowModal(false);
    setEditingNote(null);
  };

  const handleDeleteNote = async (noteId) => {
    const result = await onDeleteNote(noteId);
    if ("data" in result) {
      message.success(`${entityName} ${itemName.toLowerCase()} deleted successfully`);
    }
  };

  // Search handlers
  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleTableChange = (paginationParams) => {
    setPagination({
      current: paginationParams.current,
      pageSize: paginationParams.pageSize,
    });
  };

  const columns = [
    {
      title: createdAtLabel,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (text) => formatDate(text),
    },
    {
      title: createdByLabel,
      dataIndex: "createdByName",
      key: "createdByName",
      width: 140,
      ...getColumnSearchProps("createdBy", createdBySearchRef, handleSearch, handleReset),
      onFilter: (value, record) => {
        const userName = record.createdByName || "";
        return userName.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 110,
      sorter: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (text) => formatDate(text),
    },
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      width: 140,
      ...getColumnSearchProps("updatedBy", updatedBySearchRef, handleSearch, handleReset),
      onFilter: (value, record) => {
        const userName = record.updatedByName || "";
        return userName.toLowerCase().includes(value.toLowerCase());
      },
    },
    {
      title: messageLabel,
      dataIndex: "message",
      key: "message",
      width: 500,
      render: (text) => <TruncatedText text={text} maxLength={50} />,
      ...getColumnSearchProps("message", messageSearchRef, handleSearch, handleReset),
    },
    {
      title: "Actions",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditNote(record)}
          />
          <Popconfirm
            title={`Delete ${entityName} ${itemName}`}
            description={`Are you sure you want to delete this ${entityName.toLowerCase()} ${itemName.toLowerCase()}?`}
            onConfirm={() => handleDeleteNote(record.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <StyledDeleteButton type="text" size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <StyledModal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      maskClosable={false}
      width="90%"
    >
      {/* Add New Item Button */}
      <StyledButton type="link" icon={<PlusOutlined />} onClick={handleAddNote}>
        Add {entityName} {itemName}
      </StyledButton>

      {/* Notes Table */}
      <TableContainer>
        <StyledTable
          columns={columns}
          dataSource={notes}
          loading={isLoading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["50", "100"],
            showQuickJumper: false,
            showTotal: (total, range) =>
              `${range[0]}-${
                range[1]
              } of ${total} ${entityName.toLowerCase()} ${itemName.toLowerCase()}s`,
          }}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
        />
      </TableContainer>

      {/* Add/Edit Item Modal */}
      <StyledAddModal
        title={!editingNote ? `Add ${entityName} ${itemName}` : `Edit ${entityName} ${itemName}`}
        open={showModal}
        onCancel={handleCancelModal}
        footer={null}
        width={600}
        maskClosable={false}
        getContainer={false}
        centered
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="message"
            rules={[
              { required: true, message: "Please enter a message" },
              { min: 1, message: "Message cannot be empty" },
            ]}
          >
            <StyledTextArea placeholder={`Enter your ${itemName.toLowerCase()} here...`} />
          </Form.Item>
          <StyledFormItem>
            <Space>
              <Button onClick={handleCancelModal}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={!editingNote ? isCreating : isUpdating}
              >
                {!editingNote ? "Create" : "Update"}
              </Button>
            </Space>
          </StyledFormItem>
        </Form>
      </StyledAddModal>
    </StyledModal>
  );
}

export default EntityNotesTableModal;
